import { Routes } from '@angular/router';
import { authGuard, roleGuard } from './core/guards/auth.guard';
import { homeRedirectGuard } from './core/guards/home-redirect.guard';

export const routes: Routes = [
  {
    path: '',
    canActivate: [homeRedirectGuard],
    loadComponent: () => import('./features/catalog/catalog').then(m => m.CatalogComponent),
    pathMatch: 'full'
  },
  {
    path: 'info',
    loadComponent: () => import('./features/landing/landing').then(m => m.LandingComponent)
  },
  {
    path: 'catalog',
    redirectTo: '',
    pathMatch: 'full'
  },
  {
    path: 'catalogo',
    redirectTo: '',
    pathMatch: 'full'
  },
  {
    path: 'posibles-clientes',
    loadComponent: () => import('./features/lead-form/lead-form').then(m => m.LeadFormComponent)
  },
  {
    path: 'posibles-domiciliarios',
    loadComponent: () => import('./features/delivery-form/delivery-form').then(m => m.DeliveryFormComponent)
  },
  {
    path: 'centro-ayuda',
    loadComponent: () => import('./features/info/help-center/help-center').then(m => m.HelpCenterComponent)
  },
  {
    path: 'contacto',
    loadComponent: () => import('./features/info/contact/contact').then(m => m.ContactComponent)
  },
  {
    path: 'aviso-legal',
    loadComponent: () => import('./features/info/legal-notice/legal-notice').then(m => m.LegalNoticeComponent)
  },
  {
    path: 'privacidad',
    loadComponent: () => import('./features/info/privacy/privacy').then(m => m.PrivacyComponent)
  },
  {
    path: 'precios',
    loadComponent: () => import('./features/info/pricing/pricing').then(m => m.PricingComponent)
  },
  {
    path: 'terminos',
    loadComponent: () => import('./features/info/terms/terms').then(m => m.TermsComponent)
  },
  {
    path: 'terminos-software',
    loadComponent: () => import('./features/info/terms-software/terms-software').then(m => m.TermsSoftwareComponent)
  },
  {
    path: 'superadmin/login',
    loadComponent: () => import('./features/superadmin/login/superadmin-login').then(m => m.SuperAdminLogin)
  },
  {
    // Publica: link de activacion por WhatsApp para un SuperAdmin recien creado
    // (setea password + enrola TOTP, ver Fase C). Sin auth.
    path: 'superadmin/activate',
    loadComponent: () => import('./features/superadmin/activate/superadmin-activate').then(m => m.SuperadminActivate)
  },
  {
    // Publica: link de reset de password por WhatsApp. NO toca el TOTP. Sin auth.
    path: 'superadmin/reset-password',
    loadComponent: () => import('./features/superadmin/reset-password/superadmin-reset-password').then(m => m.SuperadminResetPassword)
  },
  {
    path: 'restaurant/login',
    loadComponent: () => import('./features/restaurant/login/restaurant-login').then(m => m.RestaurantLogin)
  },
  {
    // Segundo paso del login multi-cuenta (mismo email+password en 2+ restaurantes). Sin auth
    // (todavia no hay JWT): las credenciales llegan por PendingAccountSelectionService, en
    // memoria, nunca por la URL. Ver restaurant-login.ts:goToAccountSelection.
    path: 'restaurant/login/seleccionar-cuenta',
    loadComponent: () => import('./features/restaurant/login/select-account/select-account').then(m => m.SelectAccount)
  },
  {
    path: 'superadmin',
    canActivate: [authGuard, roleGuard],
    data: { role: 'SuperAdmin' },
    loadChildren: () => import('./features/superadmin/superadmin.routes').then(m => m.SUPERADMIN_ROUTES)
  },
  {
    path: 'owner',
    canActivate: [authGuard, roleGuard],
    data: { roles: ['Owner', 'Manager', 'Contador'] },
    loadChildren: () => import('./features/owner/owner.routes').then(m => m.OWNER_ROUTES)
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./shared/components/unauthorized/unauthorized').then(m => m.Unauthorized)
  },
  {
    path: 'inventory/:slug/:accessToken',
    loadComponent: () => import('./features/inventory/inventory-count-public/inventory-count-public').then(m => m.InventoryCountPublic)
  },
  {
    // Pagina publica de la encuesta de satisfaccion (sin auth). El token es Guid.
    // Va antes que :slug para no ser absorbido por el catch-all del restaurante.
    path: 'encuesta/:token',
    loadComponent: () => import('./features/public/customer-survey/customer-survey').then(m => m.CustomerSurvey)
  },
  {
    // Autocorreccion de factura electronica por QR (sin auth). El QR de la factura a
    // Consumidor Final codifica slug + token; el cliente registra sus datos fiscales.
    // Va antes que :slug para no ser absorbido por el catch-all del restaurante.
    path: 'fe/corregir/:slug/:token',
    loadComponent: () => import('./features/public/fe-self-correction/fe-self-correction.page').then(m => m.FeSelfCorrectionPage)
  },
  {
    path: ':slug/delivery',
    loadChildren: () => import('./features/delivery/delivery.routes').then(m => m.DELIVERY_ROUTES)
  },
  {
    path: ':slug/page/p/:productId',
    loadComponent: () => import('./features/restaurant/public-vitrina/product-page/vitrina-product-page').then(m => m.VitrinaProductPage)
  },
  {
    path: ':slug/page/menu',
    loadComponent: () => import('./features/restaurant/public-vitrina/menu-page/vitrina-menu-page').then(m => m.VitrinaMenuPage)
  },
  {
    // Menu moderno con URL propia y fija. `forcedTheme` fuerza el tema desde la
    // ruta (no se espera al backend), asi no aparece el visual-app unos segundos
    // antes del moderno. El visual-app vive en :slug/page/menu (tema dinamico
    // del restaurante); este enlace queda independiente para el moderno.
    path: ':slug/menum',
    data: { forcedTheme: 'modern' },
    loadComponent: () => import('./features/restaurant/public-vitrina/menu-page/vitrina-menu-page').then(m => m.VitrinaMenuPage)
  },
  {
    path: ':slug/page',
    loadComponent: () => import('./features/restaurant/public-vitrina/public-vitrina').then(m => m.PublicVitrina)
  },
  {
    // Landing tipo linktree (bio link de Instagram). Convive con :slug/page —
    // el Owner elige cual URL usar segun su preferencia. Aplica el branding
    // del MenuThemeConfig si esta configurado.
    path: ':slug/linktree',
    loadComponent: () => import('./features/restaurant/public-vitrina/linktree/vitrina-linktree').then(m => m.VitrinaLinktree)
  },
  {
    // Reserva pública del cliente (sin login, distinta del flujo asistido de staff).
    // Va antes que ':slug' — si no, restaurant.routes (loadChildren de ':slug') la
    // absorbe en su propio '**' y nunca se llega a este componente.
    path: ':slug/reservacion',
    loadComponent: () => import('./features/restaurant/public-reservation/public-reservation').then(m => m.PublicReservationComponent)
  },
  {
    // Estado de la reserva pública: por el enlace (con id), por lo último reservado en este navegador, o con
    // código + celular. Antes de ':slug' por la misma razón que ':slug/reservacion'.
    path: ':slug/reservacion/estado',
    loadComponent: () => import('./features/restaurant/public-reservation-status/public-reservation-status').then(m => m.PublicReservationStatusComponent)
  },
  {
    path: ':slug/reservacion/estado/:id',
    loadComponent: () => import('./features/restaurant/public-reservation-status/public-reservation-status').then(m => m.PublicReservationStatusComponent)
  },
  {
    path: ':slug',
    loadChildren: () => import('./features/restaurant/restaurant.routes').then(m => m.RESTAURANT_ROUTES)
  },
  {
    path: '**',
    redirectTo: ''
  }
];
